
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBilling } from '@/hooks/billing/useBilling';
import { useAdminStatus } from '@/hooks/admin/useAdminStatus';
import BillingNavbar from '@/components/billing/BillingNavbar';
import BillingCalendar from '@/components/billing/BillingCalendar';
import { FileText, Receipt, Calendar, CheckCircle } from 'lucide-react';

const BillingDashboard: React.FC = () => {
  const { estimates, invoices, receipts, contracts, isLoading } = useBilling();
  const { isAdmin } = useAdminStatus();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading billing dashboard...</p>
      </div>
    );
  }

  const stats = [
    {
      title: 'Pending Estimates',
      value: estimates.filter(e => e.status === 'pending').length,
      icon: FileText,
      color: 'text-yellow-600'
    },
    {
      title: 'Approved Estimates',
      value: estimates.filter(e => e.status === 'approved').length,
      icon: CheckCircle,
      color: 'text-green-600'
    },
    {
      title: 'Outstanding Invoices',
      value: invoices.filter(i => i.status === 'unpaid').length,
      icon: Receipt,
      color: 'text-red-600'
    },
    {
      title: 'Scheduled Invoices',
      value: invoices.filter(i => i.scheduled_date).length,
      icon: Calendar,
      color: 'text-blue-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <BillingNavbar />
      
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isAdmin ? 'Admin Billing Dashboard' : 'Billing Dashboard'}
          </h1>
          <p className="text-gray-600">
            {isAdmin 
              ? 'Manage all client estimates, invoices, and billing schedules'
              : 'View your estimates, invoices, and billing information'
            }
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Calendar View */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Invoice Calendar</CardTitle>
          </CardHeader>
          <CardContent>
            <BillingCalendar 
              invoices={invoices.filter(i => i.scheduled_date)} 
            />
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Estimates</CardTitle>
            </CardHeader>
            <CardContent>
              {estimates.slice(0, 5).length === 0 ? (
                <p className="text-gray-500">No estimates found</p>
              ) : (
                <div className="space-y-3">
                  {estimates.slice(0, 5).map((estimate) => (
                    <div key={estimate.id} className="flex justify-between items-center p-3 border rounded">
                      <div>
                        <p className="font-medium">#{estimate.id.slice(-8)}</p>
                        <p className="text-sm text-gray-600">{estimate.status}</p>
                      </div>
                      <p className="font-bold">${estimate.total.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              {invoices.slice(0, 5).length === 0 ? (
                <p className="text-gray-500">No invoices found</p>
              ) : (
                <div className="space-y-3">
                  {invoices.slice(0, 5).map((invoice) => (
                    <div key={invoice.id} className="flex justify-between items-center p-3 border rounded">
                      <div>
                        <p className="font-medium">#{invoice.id.slice(-8)}</p>
                        <p className="text-sm text-gray-600">{invoice.status}</p>
                      </div>
                      <p className="font-bold">${invoice.total.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BillingDashboard;
